import React, { useCallback } from 'react';
import {
  ClassNameMap,
  TableCell,
  TableRow as MuiTableRow,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import classnames from 'classnames';
import { TABLE_ROW_TYPES, TableColumnsIds } from './constants';
import { IColumn } from './interface';

const widthDate = 102;

const useStyles = makeStyles()(
  (theme, _params, classes: ClassNameMap<any>) => ({
    row: {
      height: 50,
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.background.paper,

        [`&:hover .${classes?.fixedRight}`]: {
          backgroundColor: theme.palette.background.paper,
          boxShadow: '-15px 0px 7px -1px rgba(248, 247, 248, 0.8)',
        },
        [`&:hover .${classes?.stickyColumn}`]: {
          backgroundColor: theme.palette.background.paper,
        },
        [`&:hover .${classes?.fixedLeftDate}`]: {
          backgroundColor: theme.palette.background.paper,
        },
        [`&:hover .${classes?.fixedLeftNumber}`]: {
          backgroundColor: theme.palette.background.paper,
        },
        [`&:hover .${classes?.stickyColumn}`]: {
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    fixedRight: {
      position: 'sticky',
      right: '0',
      background: theme.palette.background.default,
      boxShadow: '-15px 0px 7px -1px rgba(255, 255, 255, 0.8)',
    },
    stickyColumn: {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      backgroundColor: theme.palette.background.default,
      '&:hover': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    fixedLeftDate: {
      position: 'sticky',
      left: 0,
      width: widthDate,
      backgroundColor: theme.palette.background.default,
      '&:hover': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    fixedLeftNumber: {
      position: 'sticky',
      left: widthDate,
      zIndex: 1,
      backgroundColor: theme.palette.background.default,
      '&:hover': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    fixedHeadRight: {
      position: 'sticky',
      right: 0,
      background: theme.palette.background.paper,
      boxShadow: '-15px 0px 7px -1px rgba(248, 247, 248, 0.8)',
    },
    fixedHeadDateLeft: {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      background: theme.palette.background.paper,
    },
    fixedHeadNumberLeft: {
      position: 'sticky',
      left: widthDate,
      zIndex: 1,
      background: theme.palette.background.paper,
    },
    substatusColumnIncreasedWidth: {
      '& span': {
        maxWidth: 105,
      },
    },
    lineClamp2: {
      '& span': { minWidth: 125 },
      '& span p': {
        minWidth: 125,
        display: '-webkit-box',
        '-webkitLineClamp': '2',
        '-webkitBoxOrient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
      },
    },
  }),
);

interface ITableRow {
  type?: TABLE_ROW_TYPES;
  columns?: Array<IColumn>;
  rowData?: any;
  onSelect?: (a?: number | string) => void | boolean;
  onSelectAll?: () => void;
  styles?: any;
  onClickRow?: (a: number | string) => void;
}

const TableRow = ({
  type,
  columns,
  rowData,
  onSelect = () => false,
  onSelectAll = () => false,
  styles,
}: ITableRow) => {
  const { classes, cx } = useStyles();
  const onSelectTasks = useCallback(
    (id: number | string) => {
      if (type === TABLE_ROW_TYPES.HEAD) {
        return onSelectAll();
      }
      if (type === TABLE_ROW_TYPES.BODY) {
        return onSelect(id);
      }
    },
    [type],
  );

  return (
    <MuiTableRow
      className={classnames(styles?.row, classes.row)}
      onClick={() => rowData && onSelectTasks(rowData.id)}>
      {columns?.map((column: IColumn, index: number) => {
        const isSticky = column?.sticky;
        const cellKey = rowData
          ? `${type}_${rowData?.id}_${index}`
          : `${type}_${index}`;
        return (
          <TableCell
            key={cellKey}
            className={classnames(
              cx(
                styles?.cell,
                {
                  [classes.fixedHeadDateLeft]:
                    isSticky && type === TABLE_ROW_TYPES.HEAD,
                },
                {
                  [classes.stickyColumn]:
                    isSticky && type !== TABLE_ROW_TYPES.HEAD,
                },
              ),
              {
                [classes.fixedRight]: column?.id === TableColumnsIds.ACTIONS,
              },
              {
                [classes.fixedLeftDate]:
                  column?.id === TableColumnsIds.DATE &&
                  type !== TABLE_ROW_TYPES.HEAD,
              },
              {
                [classes.fixedHeadRight]:
                  column?.id === TableColumnsIds.EXPANDED_ITEM &&
                  type === TABLE_ROW_TYPES.HEAD,
              },
              {
                [classes.fixedHeadDateLeft]:
                  column.id === TableColumnsIds.DATE &&
                  type === TABLE_ROW_TYPES.HEAD,
              },
              {
                [classes.fixedLeftNumber]:
                  column.id === TableColumnsIds.NUMBER &&
                  type !== TABLE_ROW_TYPES.HEAD,
              },
              {
                [classes.fixedHeadNumberLeft]:
                  column.id === TableColumnsIds.NUMBER &&
                  type === TABLE_ROW_TYPES.HEAD,
              },
            )}
            align={column?.align}
            style={{
              width: column.width,
            }}>
            {column.cellReducer && column.cellReducer(rowData || column)}
          </TableCell>
        );
      })}
    </MuiTableRow>
  );
};

export default TableRow;
